import pluralize from "pluralize";
import { useMemo } from "react";

import {
  CATEGORY_ESCAPE_ROOM,
  CATEGORY_HAUNT,
} from "@/components/game-categories/constants";
import { locationName } from "@/helpers/games";

import type { LocationType } from "../types";

export default function useMetaProps(location: LocationType) {
  const games = useMemo(() => location.games, [location]);

  const { gamesCount, isAllEscapeRooms, isAllHaunts } = useMemo(() => {
    const primaryCategories = games.map(
      (game) => game.primaryCategory?.parent?.slug
    );

    return {
      gamesCount: games.length,
      isAllEscapeRooms: primaryCategories.every(
        (category) => category === CATEGORY_ESCAPE_ROOM
      ),
      isAllHaunts: primaryCategories.every(
        (category) => category === CATEGORY_HAUNT
      ),
    };
  }, [games]);

  const renderLocation = useMemo(() => {
    if (!location.games.some((game) => game.kind !== "VER")) {
      return "ONLINE";
    }

    const neighborhood = locationName(location.address, "neighborhood", true);
    const country = locationName(location.address, "country");
    const city = locationName(location.address, "locality");
    const state = locationName(
      location.address,
      "administrative_area_level_1",
      true
    );

    const nameParts: string[] = [];

    if (neighborhood) {
      nameParts.push(neighborhood);
    }
    if (city) {
      nameParts.push(city);
    }
    if (country === "United States") {
      if (state) {
        nameParts.push(state);
      }
    } else if (country) {
      nameParts.push(country);
    }

    return nameParts.join(", ");
  }, [location]);

  const description = useMemo(() => {
    const metaDescription: string[] = [];

    metaDescription.push(location.displayName || location.company.name);

    if (isAllEscapeRooms) {
      metaDescription.push("is an escape room in");
    } else if (isAllHaunts) {
      metaDescription.push("is a haunted attraction in");
    } else {
      metaDescription.push("in");
    }

    metaDescription.push(`${renderLocation}, with`);
    metaDescription.push(gamesCount.toString());
    metaDescription.push(pluralize("experience", gamesCount));

    const route = location.address.address_components.find(({ types }) =>
      types.includes("route")
    );

    if (route) {
      const streetNumber = location.address.address_components.find(
        ({ types }) => types.includes("street_number")
      );
      const subpremise = location.address.address_components.find(({ types }) =>
        types.includes("subpremise")
      );

      metaDescription.push("located at");
      if (streetNumber) {
        metaDescription.push(streetNumber.short_name);
      }
      metaDescription.push(route.short_name);
      if (subpremise) {
        metaDescription.push(`#${subpremise.short_name}`);
      }
    }

    return `${metaDescription.join(" ")}.`;
  }, [location, isAllEscapeRooms, isAllHaunts, renderLocation, gamesCount]);

  return {
    description,
    title: `${location.displayName || location.company.name} - ${location.locality.locality}, ${
      location.locality.region
    }`,
  };
}
